<template>
  <figure class="bzc-chart" :data-kind="chart.kind" data-test="ask-bizminer-chart">
    <figcaption class="bzc-chart-title">{{ chart.title }}</figcaption>
    <div v-if="chart.unit" class="bzc-chart-unit">Values in {{ chart.unit }}</div>
    <ul v-if="model.series.length > 1" class="bzc-chart-legend">
      <li v-for="(s, i) in model.series" :key="i"><span class="bzc-chart-dot" :style="{ background: color(i) }"></span>{{ s.name }}</li>
    </ul>
    <!-- Hover (or tap) a point: value, unit and the source report. -->
    <div class="bzc-chart-plot" role="img" :aria-label="aria">
      <apexchart :type="chart.kind" width="100%" :height="height" :options="options" :series="apexSeries"></apexchart>
    </div>
    <div v-if="model.sources.length === 1" class="bzc-chart-src" data-test="ask-bizminer-chart-source">Source: {{ model.sources[0] }}</div>
    <details v-else-if="model.sources.length > 1" class="bzc-chart-src" data-test="ask-bizminer-chart-source">
      <summary>Sources ({{ model.sources.length }})</summary>
      <div v-for="src in model.sources" :key="src" class="bzc-chart-src-row">{{ src }}</div>
    </details>
    <p v-if="chart.note" class="bzc-chart-note" data-test="ask-bizminer-chart-note">{{ chart.note }}</p>
  </figure>
</template>

<script>
// One chart of an Ask Bizminer answer (CHAT-19), drawn with ApexCharts (already
// a dependency of the app). The chart comes from chatCore.normalizeChart, so
// every value is a finite number and all text is plain. Apex takes HTML in only
// two places, the tooltip and the legend: the legend is ours (Vue text), the
// tooltip is built by chatTooltipHtml, which escapes every string. Axis labels
// are SVG text.
import VueApexCharts from 'vue-apexcharts';
import { chartModel, chartTooltipHtml, formatAxisValue } from './chatCore';

const PALETTE = ['#003666', '#0cbcf2', '#7041e5', '#2d6731', '#e0a100', '#b8c3d0', '#a3261c', '#beaeff'];

export default {
  name: 'chat-chart',
  components: { apexchart: VueApexCharts },
  props: {
    chart: { type: Object, required: true },
    // The citations of the same message: they name each point's source report.
    citations: { type: Array, default: () => [] },
  },
  computed: {
    model() {
      return chartModel(this.chart, this.citations);
    },
    apexSeries() {
      return this.model.series.map((s) => ({ name: s.name, data: s.data }));
    },
    height() {
      const m = this.model;
      return m.horizontal ? Math.max(170, m.categories.length * m.series.length * 24 + 50) : 210;
    },
    aria() {
      const n = this.model.categories.length;
      return this.chart.title + ': ' + this.chart.kind + ' chart, ' + this.model.series.length + ' series, ' + n + ' labels';
    },
    options() {
      const m = this.model;
      const unit = this.chart.unit;
      const bar = this.chart.kind === 'bar';
      const label = { style: { fontSize: '11px', colors: '#5b6575', fontFamily: 'Roboto, Arial, sans-serif' } };
      const values = { ...label, formatter: (v) => formatAxisValue(v, unit) };
      return {
        chart: {
          type: this.chart.kind,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'Roboto, Arial, sans-serif',
          parentHeightOffset: 0,
          selection: { enabled: false },
        },
        colors: PALETTE,
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: bar ? { show: false, width: 0 } : { width: 2.5, curve: 'straight' },
        markers: bar ? { size: 0 } : { size: 4, strokeWidth: 0, hover: { sizeOffset: 3 } },
        plotOptions: { bar: { horizontal: m.horizontal, columnWidth: '62%', barHeight: '70%', borderRadius: 2 } },
        grid: { borderColor: '#e6ebf1', padding: { left: 4, right: 18, top: 0, bottom: 0 } },
        xaxis: {
          categories: m.categories,
          tooltip: { enabled: false },
          axisTicks: { show: false },
          axisBorder: { color: '#d7dde5' },
          labels: m.horizontal ? { ...values } : { ...label, rotate: -35, rotateAlways: false, hideOverlapLabels: true, trim: true },
        },
        yaxis: m.horizontal
          ? { labels: { ...label, maxWidth: 110 } }
          : { tickAmount: 4, forceNiceScale: true, labels: { ...values, minWidth: 28 } },
        tooltip: {
          // Hover or tap one point at a time; the content is built (and escaped) by chatTooltipHtml.
          shared: false,
          intersect: bar,
          followCursor: false,
          custom: ({ seriesIndex, dataPointIndex }) => chartTooltipHtml(m, seriesIndex, dataPointIndex),
        },
        states: { active: { filter: { type: 'none' } } },
        noData: { text: '' },
      };
    },
  },
  mounted() {
    // Apex flips a tooltip to the left of a point that is near the right edge, but never
    // checks the left edge: in a narrow phone chart it ends up off screen. Keep it inside the chart.
    if (typeof MutationObserver === 'undefined') return;
    this.ttObserver = new MutationObserver((list) => list.forEach((m) => this.keepInside(m.target)));
    this.ttObserver.observe(this.$el, { subtree: true, attributes: true, attributeFilter: ['style'] });
  },
  beforeDestroy() {
    if (this.ttObserver) this.ttObserver.disconnect();
    this.ttObserver = null;
  },
  methods: {
    color(i) {
      return PALETTE[i % PALETTE.length];
    },
    keepInside(el) {
      if (!el || !el.classList || !el.classList.contains('apexcharts-tooltip') || !el.parentElement) return;
      const left = parseFloat(el.style.left);
      if (!isFinite(left)) return;
      const next = Math.min(Math.max(left, 0), Math.max(0, el.parentElement.clientWidth - el.offsetWidth));
      if (next !== left) el.style.left = next + 'px';
    },
  },
};
</script>

<style lang="scss">
.bzc-chart {
  margin: 0; min-width: 0; max-width: 100%; box-sizing: border-box; padding: 10px 10px 8px;
  border: 1px solid #dfe5ec; border-radius: 8px; background: #fff; display: flex; flex-direction: column; gap: 4px;
}
.bzc-chart-title { font-family: Inter, Roboto, sans-serif; font-size: 13.5px; font-weight: 600; line-height: 1.3; color: #003666; overflow-wrap: anywhere; }
.bzc-chart-unit { font-size: 11.5px; color: #6b7686; }
.bzc-chart-legend {
  list-style: none; margin: 2px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 2px 12px; font-size: 11.5px; color: #3c4656;
  li { display: inline-flex; align-items: center; gap: 5px; min-width: 0; overflow-wrap: anywhere; }
}
.bzc-chart-dot { flex: 0 0 auto; width: 9px; height: 9px; border-radius: 5px; }
.bzc-chart-plot { min-width: 0; max-width: 100%; margin: 0 -4px; }
.bzc-chart-src { font-size: 11px; line-height: 1.35; color: #6b7686; overflow-wrap: anywhere; }
.bzc-chart-src summary { cursor: pointer; padding: 4px 0; &:focus-visible { outline: 2px solid #0cbcf2; outline-offset: 2px; } }
.bzc-chart-src-row { padding: 2px 0 2px 10px; border-left: 2px solid #e6ebf1; margin-top: 3px; }
.bzc-chart-note { margin: 2px 0 0; font-size: 11.5px; line-height: 1.4; color: #6a4a00; background: #fff4d6; border-radius: 6px; padding: 5px 8px; }

// Apex builds the tooltip itself; the content comes from chatTooltipHtml (escaped).
.bzc-chart .apexcharts-tooltip {
  border: 1px solid #c9d3de; border-radius: 8px; box-shadow: 0 4px 14px rgba(0, 30, 60, 0.18); background: #fff; color: #22303f;
  max-width: 230px; white-space: normal; overflow: visible; font-family: Roboto, Arial, sans-serif;
}
.bzc-tt { padding: 7px 10px; font-size: 12px; line-height: 1.35; overflow-wrap: anywhere; }
.bzc-tt-series { font-weight: 700; color: #003666; }
.bzc-tt-label { color: #5b6575; }
.bzc-tt-value { margin: 2px 0; font-size: 15px; font-weight: 700; color: #13213c; }
.bzc-tt-src { font-size: 11px; color: #6b7686; }

@media (max-width: 600px) {
  .bzc-chart { padding: 10px 8px 8px; }
  .bzc-chart .apexcharts-tooltip { max-width: 200px; }
}
</style>
